import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const FAMILY_INFO_FOLDER_URL = 'https://drive.google.com/drive/folders/1IhuSqJ4gE83sr-zDLhdCbbFjXb13ckQp?usp=drive_link';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!isHome) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 80);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHome]);

  const handleStaffClick = () => {
    setIsPasswordModalOpen(true);
    setPasswordInput('');
    setPasswordError('');
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === 'psms108staff') {
      window.open('https://sites.google.com/schools.nyc.gov/soaresources/home', '_blank');
      setIsPasswordModalOpen(false);
      setPasswordInput('');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPasswordInput('');
    }
  };

  const handleModalClose = () => {
    setIsPasswordModalOpen(false);
    setPasswordInput('');
    setPasswordError('');
  };

  const navItems = [
    {
      name: 'Home',
      href: '/'
    },
    {
      name: 'About Us',
      href: '/about',
      dropdown: [
        { name: 'Mission Statement', href: '/mission-statement' },
        { name: 'Survey Results', href: '/survey-results' },
        { name: 'Meet The Staff', href: '/meet-the-staff' },
        { name: 'Calendar', href: '/calendar' },
        { name: 'Gallery', href: '/gallery' },
      ]
    },
    {
      name: 'Programs',
      href: '/programs',
      dropdown: [
        { name: 'Union Settlement', href: '/union-settlement' },
        { name: 'Afterschool Programs', href: '/afterschool-programs' },
        {
          name: 'Wellness',
          href: '/wellness',
          dropdown: [
            { name: 'Lunch', href: 'https://www.schools.nyc.gov/school-life/food/menus/school-lunch-meals', external: true },
            { name: 'Instagram - @the108bite', href: 'https://www.instagram.com/the108bite/', external: true },
          ]
        },
      ]
    },
    {
      name: 'Resources',
      href: '/resources',
      dropdown: [
        {
          name: 'Family Resources',
          href: '/family-resources',
          dropdown: [
            { name: 'JupiterGrades', href: 'https://login.jupitered.com/login/', external: true },
            { name: 'Family Handbook', href: 'https://docs.google.com/document/d/1bUZ642ztGGjug9ERXbBcWsMW5XvZUx42s1ZjWA-3w1w/edit?usp=sharing', external: true },
            { name: 'Family Resource Drive', href: 'https://drive.google.com/drive/folders/1IQwbXVetsm5Vdq2Nh-Q-JACAds5mXnih?usp=drive_link', external: true },
            { name: 'Emergency Contact Form', href: 'https://nycdoe-my.sharepoint.com/my?viewid=fb3c98cf%2Db307%2D43ca%2Dad28%2De6d68878e75c&id=%2Fpersonal%2Fjtegano%5Fschools%5Fnyc%5Fgov%2FDocuments%2FSOA%20One%20Drive%2FRegistration%2FForms%2Femergency%2Dcontact%2Dcard%2Epdf&parent=%2Fpersonal%2Fjtegano%5Fschools%5Fnyc%5Fgov%2FDocuments%2FSOA%20One%20Drive%2FRegistration%2FForms', external: true },
            { name: 'NYC Students Account', href: 'https://www.schoolsaccount.nyc/', external: true },
            { name: 'How to Access Report Card', href: 'https://drive.google.com/file/d/1BLrvOuw79E2IF5OZ0Pt1eU6TqoeZOXmv/view?usp=drive_link', external: true },
            { name: 'Myschools', href: 'https://myschools.nyc/en/', external: true },
            { name: 'DOE Home Page', href: 'https://www.schools.nyc.gov/', external: true },
            { name: 'Transportation Info', href: 'https://www.schools.nyc.gov/school-life/transportation/transportation-overview', external: true },
          ]
        },
        {
          name: 'Registration Forms',
          href: '/registration-forms',
          dropdown: [
            { name: 'Student Registration Form', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/student-registration-form63047bc43fc9461192726bb4a9c74e93.pdf?csf=1&web=1&e=SDwDwJ', external: true },
            { name: 'Housing Questionaire', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/housing-questionnaire%20English.pdf?csf=1&web=1&e=ytiOnc', external: true },
            { name: 'New Kindergarten Admit Questionnaire', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/new-kindergarten-admit-questionnaire.pdf?csf=1&web=1&e=OUAWuC', external: true },
            { name: 'Non-Parent Affidavit', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/non-parent-affidavit-english.pdf?csf=1&web=1&e=WpQnMr', external: true },
            { name: 'Parent Student Ethnic and Race Identification', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/Parent%20Student%20Ethnic%20and%20Race%20identification-english.pdf?csf=1&web=1&e=S98YIi', external: true },
            { name: 'Parent Affidavit', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/parent-affidavit-english.pdf?csf=1&web=1&e=ktjje2', external: true },
            { name: 'Parent Affidavit of Residency', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/parent-affidavit-of-residency-english.pdf?csf=1&web=1&e=U67Tbg', external: true },
            { name: 'Third Party Statement of Residency', href: 'https://nycdoe-my.sharepoint.com/:b:/r/personal/jtegano_schools_nyc_gov/Documents/SOA%20One%20Drive/Registration/Forms/third-party-statement-of-residency.pdf?csf=1&web=1&e=7jy57q', external: true },
          ]
        },
        { name: 'Student Resources', href: '/student-resources' },
        { name: 'Supply List', href: 'https://drive.google.com/drive/folders/1Ye_6J45fzNescbBsTFIWH33o8OwsMhrB?usp=drive_link', external: true },
        { name: 'SLT Minutes and Agenda', href: 'https://drive.google.com/drive/folders/1dNBzLU61DlN9P4JokWiD4GDQpZKzYM9G?usp=drive_link', external: true },
      ]
    },
    {
      name: 'Community',
      href: '/community',
      dropdown: [
        {
          name: 'PTA',
          href: '/pta',
          dropdown: [
            { name: 'Meet The PTA', href: '/meet-the-pta' },
            { name: 'Meeting Minutes', href: '/pta-meeting-minutes' },
            { name: 'Agenda', href: 'https://drive.google.com/drive/folders/1grMGuvm6ddf7g2I-ShIGH5eqfaIbV3WQ?usp=sharing', external: true },
            { name: 'Calendar', href: '/pta-calendar' },
          ]
        },
        {
          name: 'Student Government',
          href: '/student-government',
        },
        {
          name: 'Alumni',
          href: '/alumni',
          dropdown: [
            { name: 'Class of 2025', href: '/alumni-2025' },
            { name: 'Class of 2024', href: '/alumni-2024' },
            { name: 'Class of 2023', href: '/alumni-2023' },
            { name: 'Class of 2022', href: '/alumni-2022' },
            { name: 'Class of 2021', href: '/alumni-2021' },
            { name: 'Class of 2011-2020', href: '/alumni-2011-2020' },
          ]
        },
      ]
    },
    {
      name: 'Staff',
      requiresPassword: true
    },
  ];

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Unscrolled, the bar sits over the hero video. It carries its own real
  // (non-image) dark background so the white nav text always has a solid,
  // computable backdrop — both for contrast checkers and for anyone loading
  // the page before the video paints.
  return (
    <nav aria-label="Main navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled
        ? 'bg-white shadow-lg border-b border-white/20'
        : 'bg-black/80 backdrop-blur-md border-b border-white/10'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/assets/images/logo.png"
              alt="PSMS 108 Logo"
              className="h-16 w-16 object-contain transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center flex-1 justify-center space-x-8 mx-8">
            {navItems.map((item, index) => (
              <div key={index} className="relative group">
                {item.requiresPassword ? (
                  <button
                    onClick={handleStaffClick}
                    className={`py-2 text-sm font-medium transition-all duration-300 ${
                      isScrolled
                        ? 'text-brand-text-secondary hover:text-brand-primary'
                        : 'text-white/95 hover:text-white drop-shadow-sm'
                    }`}
                  >
                    {item.name}
                  </button>
                ) : item.dropdown ? (
                  <div>
                    <button
                      className={`flex items-center py-2 text-sm font-medium transition-all duration-300 ${
                        isScrolled
                          ? 'text-brand-text-secondary hover:text-brand-navy'
                          : 'text-white/95 hover:text-brand-yellow drop-shadow-sm'
                      }`}
                      onMouseEnter={() => { setActiveDropdown(index); setActiveSubDropdown(null); }}
                      aria-expanded={activeDropdown === index}
                      aria-haspopup="true"
                    >
                      {item.name}
                      <ChevronDownIcon className="ml-1 h-3 w-3" aria-hidden="true" />
                    </button>
                    {activeDropdown === index && (
                      <div
                        className="absolute top-full left-0 mt-2 w-56 bg-white shadow-2xl border border-gray-200 rounded-xl py-3 z-50 transform opacity-100 scale-100 transition-all duration-200"
                        onMouseLeave={() => { setActiveDropdown(null); setActiveSubDropdown(null); }}
                      >
                        <div className="px-3">
                          <div className="text-xs font-semibold text-brand-accent-dark uppercase tracking-wider mb-3 px-3">
                            {item.name}
                          </div>
                          {item.dropdown.map((dropdownItem, dropdownIndex) => (
                            <div key={dropdownIndex} className="relative">
                              {dropdownItem.dropdown ? (
                                <div>
                                  <button
                                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-brand-text-primary hover:text-brand-navy hover:bg-brand-yellow/10 transition-all duration-200 rounded-lg group relative overflow-hidden"
                                    onMouseEnter={() => setActiveSubDropdown(dropdownIndex)}
                                  >
                                    <div className="absolute left-0 top-0 bottom-0 w-0 bg-brand-navy group-hover:w-1 transition-all duration-200"></div>
                                    <span className="relative z-10">{dropdownItem.name}</span>
                                    <ChevronDownIcon className="ml-1 h-3 w-3 rotate-[-90deg]" aria-hidden="true" />
                                  </button>
                                  {activeSubDropdown === dropdownIndex && (
                                    <div className="absolute left-full top-0 ml-1 w-80 bg-white shadow-2xl border border-gray-200 rounded-xl py-3 z-50">
                                      <div className="px-2">
                                        <div className="text-xs font-semibold text-brand-accent-dark uppercase tracking-wider mb-2 px-4 py-1">
                                          {dropdownItem.name}
                                        </div>
                                        <div className="max-h-96 overflow-y-auto">
                                          {dropdownItem.dropdown.map((subItem, subIndex) => (
                                            subItem.external ? (
                                              <a
                                                key={subIndex}
                                                href={subItem.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block px-4 py-2.5 text-sm font-medium text-brand-text-primary hover:text-brand-navy hover:bg-brand-yellow/10 transition-all duration-200 rounded-lg group relative overflow-hidden"
                                              >
                                                <div className="absolute left-0 top-0 bottom-0 w-0 bg-brand-navy group-hover:w-1 transition-all duration-200"></div>
                                                <span className="relative z-10 pl-1">{subItem.name}</span>
                                              </a>
                                            ) : (
                                              <Link
                                                key={subIndex}
                                                to={subItem.href}
                                                className="block px-4 py-2.5 text-sm font-medium text-brand-text-primary hover:text-brand-navy hover:bg-brand-yellow/10 transition-all duration-200 rounded-lg group relative overflow-hidden"
                                              >
                                                <div className="absolute left-0 top-0 bottom-0 w-0 bg-brand-navy group-hover:w-1 transition-all duration-200"></div>
                                                <span className="relative z-10 pl-1">{subItem.name}</span>
                                              </Link>
                                            )
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : dropdownItem.external ? (
                                <a
                                  href={dropdownItem.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block px-4 py-3 text-sm font-medium text-brand-text-primary hover:text-brand-navy hover:bg-brand-yellow/10 transition-all duration-200 rounded-lg group relative overflow-hidden"
                                >
                                  <div className="absolute left-0 top-0 bottom-0 w-0 bg-brand-navy group-hover:w-1 transition-all duration-200"></div>
                                  <span className="relative z-10">{dropdownItem.name}</span>
                                </a>
                              ) : (
                                <Link
                                  to={dropdownItem.href}
                                  className="block px-4 py-3 text-sm font-medium text-brand-text-primary hover:text-brand-navy hover:bg-brand-yellow/10 transition-all duration-200 rounded-lg group relative overflow-hidden"
                                >
                                  <div className="absolute left-0 top-0 bottom-0 w-0 bg-brand-navy group-hover:w-1 transition-all duration-200"></div>
                                  <span className="relative z-10">{dropdownItem.name}</span>
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`py-2 text-sm font-medium transition-all duration-300 ${
                      isScrolled
                        ? 'text-brand-text-secondary hover:text-brand-primary'
                        : 'text-white/95 hover:text-white drop-shadow-sm'
                    }`}
                    aria-current={location.pathname === item.href ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={FAMILY_INFO_FOLDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 bg-brand-accent text-brand-navy hover:bg-brand-yellow shadow-lg"
            >
              Family Information Folder
              <span className="sr-only">(opens in a new tab)</span>
            </a>
            <Link
              to="/contact"
              className={`inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                isScrolled
                  ? 'bg-brand-primary text-white hover:bg-brand-secondary shadow-lg'
                  : 'bg-white text-brand-primary hover:bg-white shadow-lg'
              }`}
            >
              Contact Us
            </Link>
          </div>

          {/* Language selector + mobile menu button */}
          <div className="flex items-center gap-2 ml-auto lg:ml-3">
            <span id="site-language" className="inline-flex items-center"></span>
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isScrolled
                    ? 'text-brand-primary hover:bg-gray-100'
                    : 'text-white hover:bg-white/10 drop-shadow-lg'
                }`}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                aria-haspopup="true"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-xl border-t border-gray-200">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {navItems.map((item, index) => (
              <div key={index}>
                {item.requiresPassword ? (
                  <button
                    onClick={() => {
                      handleStaffClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block px-3 py-3 text-sm font-medium text-brand-text-secondary hover:text-brand-primary transition-all duration-200"
                  >
                    {item.name}
                  </button>
                ) : item.dropdown ? (
                  <div>
                    <button
                      onClick={() => handleDropdownToggle(index)}
                      className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-brand-text-secondary hover:text-brand-primary transition-all duration-200"
                      aria-expanded={activeDropdown === index}
                    >
                      {item.name}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${
                        activeDropdown === index ? 'rotate-180' : ''
                      }`} aria-hidden="true" />
                    </button>
                    {activeDropdown === index && (
                      <div className="ml-4 space-y-1">
                        {item.dropdown.map((dropdownItem, dropdownIndex) => (
                          <div key={dropdownIndex}>
                            {dropdownItem.dropdown ? (
                              <div>
                                <button
                                  onClick={() => setActiveSubDropdown(activeSubDropdown === `${index}-${dropdownIndex}` ? null : `${index}-${dropdownIndex}`)}
                                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-brand-text-muted hover:text-brand-primary transition-all duration-200"
                                  aria-expanded={activeSubDropdown === `${index}-${dropdownIndex}`}
                                >
                                  {dropdownItem.name}
                                  <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${
                                    activeSubDropdown === `${index}-${dropdownIndex}` ? 'rotate-180' : ''
                                  }`} aria-hidden="true" />
                                </button>
                                {activeSubDropdown === `${index}-${dropdownIndex}` && (
                                  <div className="ml-4 space-y-1">
                                    {dropdownItem.dropdown.map((subItem, subIndex) => (
                                      subItem.external ? (
                                        <a
                                          key={subIndex}
                                          href={subItem.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block px-3 py-2 text-xs text-brand-text-muted hover:text-brand-primary transition-all duration-200"
                                          onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                          {subItem.name}
                                        </a>
                                      ) : (
                                        <Link
                                          key={subIndex}
                                          to={subItem.href}
                                          className="block px-3 py-2 text-xs text-brand-text-muted hover:text-brand-primary transition-all duration-200"
                                          onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                          {subItem.name}
                                        </Link>
                                      )
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : dropdownItem.external ? (
                              <a
                                href={dropdownItem.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-3 py-2.5 text-sm text-brand-text-muted hover:text-brand-primary transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {dropdownItem.name}
                              </a>
                            ) : (
                              <Link
                                to={dropdownItem.href}
                                className="block px-3 py-2.5 text-sm text-brand-text-muted hover:text-brand-primary transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {dropdownItem.name}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className="block px-3 py-3 text-sm font-medium text-brand-text-secondary hover:text-brand-primary transition-all duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-current={location.pathname === item.href ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            {/* Mobile CTA Buttons */}
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
              <a
                href={FAMILY_INFO_FOLDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center bg-brand-accent text-brand-navy font-medium px-6 py-3 transition-all duration-300 hover:bg-brand-yellow rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Family Information Folder
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <Link
                to="/contact"
                className="block w-full text-center bg-brand-primary text-white font-medium px-6 py-3 transition-all duration-300 hover:bg-brand-secondary rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="password-modal-title">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8 relative">
            <button
              onClick={handleModalClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            <div className="text-center mb-6">
              <h2 id="password-modal-title" className="text-2xl font-bold text-brand-navy mb-2">Staff Portal</h2>
              <p className="text-sm text-gray-600">Please enter the password to access staff resources</p>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-6">
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                  autoFocus
                  aria-label="Staff portal password"
                  aria-describedby={passwordError ? 'password-error' : undefined}
                />
                {passwordError && (
                  <p id="password-error" className="mt-2 text-sm text-red-600">{passwordError}</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="flex-1 px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 text-sm font-medium text-white bg-brand-primary hover:bg-brand-secondary rounded-lg transition-all duration-200 shadow-lg"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;